import { Fragment } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router';

import { SoftwareOnboardingFooter } from 'src/components/SoftwareOnboarding';
import { Button } from 'src/components/ui/button';
import { useSoftwareEscrowOnboarding, useSoftwareEscrow } from 'src/context/software-escrow';
import { AgreementGenerateDialog, AgreementReviewDialog } from 'src/containers/SoftwareEscrow/Agreement';

type IFooterButton = {
  onClick: () => void;
  disabled?: boolean;
  variant?: 'default' | 'secondary';
  children: React.ReactNode;
};

const FooterButton = ({ onClick, disabled, variant, children }: IFooterButton) => (
  <Button onClick={onClick} disabled={disabled} variant={variant}>
    {children}
  </Button>
);

const BackButton = ({ onClick }: { onClick: () => void }) => (
  <FooterButton variant="secondary" onClick={onClick}>
    <ArrowLeft /> Back
  </FooterButton>
);

const NextButton = ({ onClick, disabled }: Pick<IFooterButton, 'onClick' | 'disabled'>) => (
  <FooterButton onClick={onClick} disabled={disabled}>
    Next <ArrowRight />
  </FooterButton>
);

const EditModeFooter = ({ id }: { id: string }) => {
  const { isNextStepDisabled } = useSoftwareEscrowOnboarding();
  const navigate = useNavigate();
  const goBackToSummary = () => navigate(`/software-escrows/onboarding/${id}/summary`);

  return (
    <SoftwareOnboardingFooter justify="end">
      <FooterButton onClick={goBackToSummary} disabled={isNextStepDisabled}>
        Back to summary <ArrowRight />
      </FooterButton>
    </SoftwareOnboardingFooter>
  );
};

const AgreementButtons = ({ isInvitedParty }: { isInvitedParty: boolean }) => {
  if (isInvitedParty)
    return (
      <AgreementReviewDialog
        triggerText={
          <Fragment>
            Submit <ArrowRight />
          </Fragment>
        }
      />
    );

  return (
    <AgreementGenerateDialog
      triggerText={
        <Fragment>
          Generate Agreement <ArrowRight />
        </Fragment>
      }
    />
  );
};

const Footer = () => {
  const { handlePrevStep, handleNextStep, totalSteps, currentStep, isNextStepDisabled, showBackButton } =
    useSoftwareEscrowOnboarding();
  const [searchParams] = useSearchParams();
  const {
    softwareEscrow: { id, currentCompanyRole, startedBy },
  } = useSoftwareEscrow();

  const isEdit = searchParams.get('isEdit') === 'true';
  if (isEdit) return <EditModeFooter id={id} />;

  const isInvitedParty = currentCompanyRole !== startedBy;

  return (
    <SoftwareOnboardingFooter justify={showBackButton ? 'between' : 'end'}>
      {showBackButton && <BackButton onClick={handlePrevStep} />}
      {currentStep === totalSteps ? (
        <AgreementButtons isInvitedParty={isInvitedParty} />
      ) : (
        <NextButton onClick={handleNextStep} disabled={isNextStepDisabled} />
      )}
    </SoftwareOnboardingFooter>
  );
};

export default Footer;
